import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from "react-redux";
import useKeypress from 'react-use-keypress';
import { toast } from 'react-toastify';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image'

import { getAnalytics, logEvent } from "firebase/analytics";

import secureLocalStorage from "react-secure-storage";

import { fetchRequest } from '../fetchRequest';
import { priceFormat } from '../utils';

const analytics = getAnalytics();

const AVATAR_PLACEHOLDER = `/assets/images/avatar_placeholder.png`;

const FoodSale = ({ pageFocused = false }) => {
    const mountedRef = useRef(false);
    const { currentUser } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);

    const [, setNfcListening] = useState(false);
    const [saleStatusCode, setSaleStatusCode] = useState(null);
    // Use a ref to avoid stale closure issues in the keypress handler
    const nfcCodeRef = useRef('');
    const [tempNfcCode, setTempNfcCode] = useState('');
    const [saleDuplicateModal, setSaleDuplicateModal] = useState(false);
    const [sale, setSale] = useState(null);
    const [saleUser, setSaleUser] = useState(null);

    const [lastUsages, setLastUsages] = useState([]);
    const [devNfcInput, setDevNfcInput] = useState('');

    // Ref so setTimeout callbacks can check if component is still mounted
    const nfcListeningRef = useRef(false);

    const setNfcListeningBoth = (val) => {
        nfcListeningRef.current = val;
        setNfcListening(val);
    };

    const loadUsageHistory = () => {
        const usageHistories = secureLocalStorage.getItem("eKioskFoodUsages");
        setLastUsages(usageHistories ? JSON.parse(usageHistories) : []);
    };

    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            loadUsageHistory();
        }
        setNfcListeningBoth(pageFocused);
    }, [pageFocused]);

    // Cleanup all pending timers on unmount
    const timersRef = useRef([]);
    useEffect(() => {
        const timers = timersRef.current;
        return () => {
            mountedRef.current = false;
            timers.forEach(clearTimeout);
        };
    }, []);

    const safeTimeout = (fn, delay) => {
        const id = setTimeout(() => {
            if (mountedRef.current) fn();
        }, delay);
        timersRef.current.push(id);
    };

    const updateLastUsages = (userObj = null, saleObj = null) => {
        try {
            const updated = [
                {
                    avatar: userObj?.avatar,
                    firstName: userObj?.firstName,
                    lastName: userObj?.lastName,
                    code: userObj?.code,
                    date: saleObj?.date,
                    type: saleObj?.type
                },
                ...lastUsages
            ].slice(0, 5);
            secureLocalStorage.setItem("eKioskFoodUsages", JSON.stringify(updated));
            setLastUsages(updated);
        } catch (e) {
            console.log('e', e);
        }
    };

    const submitLog = (eventKey = 'error', params = {}) => {
        try {
            logEvent(analytics, eventKey, params);
        } catch (e) {}
    };

    const playDenied = (nfc) => {
        try {
            new Audio('./assets/denied.mp3').play();
        } catch (e) {
            submitLog('error', { nfc, msg: e });
        }
    };

    const handleDenied = (statusCode, nfc, delay = 3500) => {
        playDenied(nfc);
        if (statusCode) setSaleStatusCode(statusCode);
        setTempNfcCode(null);
        safeTimeout(() => {
            setNfcListeningBoth(true);
            setSaleStatusCode(null);
            setSaleUser(null);
        }, delay);
    };

    const submitUsage = (nfc = null, force = false) => {
        if (!nfc) return;

        setTempNfcCode(nfc);
        setLoading(true);
        nfcCodeRef.current = '';
        setSaleStatusCode(null);
        setNfcListeningBoth(false);
        setSale(null);
        setSaleUser(null);

        const bodyParams = {
            school: currentUser?.school,
            nfcCode: nfc,
            force: force ? 1 : 0
        };

        fetchRequest("app/kiosk/sale/usage", 'POST', bodyParams)
            .then(res => {
                if (!mountedRef.current) return;
                setLoading(false);

                if (res?.success) {
                    setSale(res?.sale);
                    setSaleUser(res?.user);

                    if (!res?.user) {
                        handleDenied("CARD_NOT_FOUND", nfc);
                        return;
                    }

                    if (!res?.sale) {
                        handleDenied("SALE_NOT_FOUND", nfc);
                        return;
                    }

                    if (res.sale.used) {
                        try {
                            new Audio('./assets/success.mp3').play();
                        } catch (e) {
                            console.log('error:submitUsage mp3', e);
                            submitLog('error', { nfc, msg: e });
                        }

                        setTempNfcCode(null);
                        setSaleStatusCode("SUCCESS");

                        if (currentUser?.showHistory) {
                            updateLastUsages(res.user, res.sale);
                        }

                        if (currentUser?.receipt) {
                            try {
                                print(res.sale, res.user, res.fontSize);
                            } catch (e) {
                                console.log('error:submitUsage print', e);
                                submitLog('error', { nfc, msg: e });
                            }
                        }

                        safeTimeout(() => {
                            setNfcListeningBoth(true);
                            setSaleStatusCode(null);
                            setSaleUser(null);
                            setSale(null);
                        }, 3500);
                        return;
                    }

                    if (res.sale.canUse) {
                        if (res.sale.existing) {
                            setSaleDuplicateModal(true);
                        } else {
                            handleDenied(null, nfc, 2500);
                        }
                    } else {
                        handleDenied(res.sale.statusCode, nfc);
                    }
                } else {
                    handleDenied("CARD_NOT_FOUND", nfc);
                }
            })
            .catch(e => {
                if (!mountedRef.current) return;
                playDenied(nfc);
                submitLog('error', { nfc, msg: e });
                setTempNfcCode(null);
                setLoading(false);
                setNfcListeningBoth(true);

                toast.warning('Хүсэлт илгээхэд алдаа гарлаа', {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false
                });
            });
    };

    useKeypress(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Enter'], (event) => {
        if (!nfcListeningRef.current) return;
        if (event.key?.toLowerCase() === 'enter') {
            const code = nfcCodeRef.current;
            nfcCodeRef.current = '';
            submitUsage(code);
        } else {
            nfcCodeRef.current = nfcCodeRef.current + event.key;
        }
    });

    const renderHomeImage = (status = null) => {
        switch (status) {
            case "SUCCESS":
                return `/assets/images/success.png`;
            case "DUPLICATED":
                return `/assets/images/duplicated.png`;
            case "INSUFFICIENT_BALANCE":
            case "SALE_NOT_FOUND":
                return `/assets/images/insufficient_balance.png`;
            case "CARD_NOT_FOUND":
                return `/assets/images/card_not_found.png`;
            default:
                return `/assets/images/welcome.png`;
        }
    };

    const renderHomeDescription = (status = null) => {
        switch (status) {
            case "SUCCESS":
                return <p className='basket-label m-0 home-description text-green' style={{ display: 'block' }}><span>Сайхан хооллоорой</span><span>Have a nice meal</span></p>
            case "INSUFFICIENT_BALANCE":
                return <p className='basket-label m-0 home-description text-brown' style={{ display: 'block' }}><span>Үлдэгдэл хүрэлцэхгүй байна!</span><span>Not enough balance!</span></p>
            case "DUPLICATED":
                return <p className='basket-label m-0 home-description text-brown' style={{ display: 'block' }}><span>Өнөөдөр үйлчилгээг ашигласан байна!</span><span>You have already used today!</span></p>
            case "SALE_NOT_FOUND":
                return <p className='basket-label m-0 home-description text-brown' style={{ display: 'block' }}><span>Хоолонд орох эрхгүй хэрэглэгч байна!</span><span>You are unable to use this service!</span></p>
            case "CARD_NOT_FOUND":
                return <p className='basket-label m-0 home-description' style={{ display: 'block' }}><span>Картын мэдээлэл олдсонгүй!</span><span>Your card is invalid!</span></p>
            default:
                return <p className='basket-label m-0 home-description' style={{ display: 'block' }}><span>Картаа уншуулаарай</span><span>Please put your ID card</span></p>
        }
    };

    const print = (saleObj = {}, userObj = {}, fontSize = 10) => {
        const win = window.open('', '_blank');
        if (!win) {
            console.log('Print failed: popup blocked');
            submitLog('error', { msg: 'Print failed: popup blocked' });
            return;
        }

        const style = `<style>
            .schoolLogo {width:30%; height:20%}
            .logoLine{display:flex}
            .dateLogoContainer{margin-left:5%}
            .rowContainer{display:flex; width: 100%;}
            .row-class{width: 20%; padding: 0.5em 1em;}
            .row-studentcode{width: 30%; padding: 0.5em 1em;}
            .row-studentname{width: 40%; padding: 0.5em 1em; flex-wrap: wrap; word-wrap: break-word;}
            .row-food{width:30%; padding: 0.5em 1em}
            .row-foodNumber{width:20%; padding: 0.5em 1em}
            .row-foodPrice{width:20%; padding: 0.5em 1em}
            .row-foodTotal{width:30%; padding: 0.5em 1em}
            .row-total{width:40%; padding: 0.5em 1em;font-weight: bold;}
            .row-totalNumber{width:30%; padding: 0.5em 1em;font-weight: bold;}
            .row-totalPrice{display:flex;justify-content: flex-end;font-weight: bold;padding: 0.5em 1em}
            @media print { @page { margin: 0; } body { margin: 0cm; }}
        </style>`;

        const userLine = [userObj?.title, userObj?.code, userObj?.firstName].filter(Boolean).join(', ');

        const html = `
            <html style="margin:0;padding:0;">
            <head>${style}</head>
            <body style="margin:0;font-family:Pinnacle,Arial,sans-serif;">
                <div style="display:flex;margin:0;">
                    <div style="font-size:${fontSize}px;word-break:break-all;text-transform:uppercase;">
                        ${currentUser?.schoolName ?? ''}
                    </div>
                </div>
                <p style="margin:0 0 10px;font-size:${fontSize}px;">${userLine}</p>
                <div style="margin:0;">
                    <p style="width:40%;margin:0;font-size:${fontSize}px;display:inline-block;font-family:Roboto;">${saleObj?.type ?? ''}</p>
                    <p style="width:20%;margin:0;display:inline-block;font-size:${fontSize}px;font-family:Roboto;">1</p>
                    <p style="width:20%;margin:0;display:inline-block;font-size:${fontSize}px;font-family:Roboto;">${priceFormat(saleObj?.price)}</p>
                    <p style="display:inline-block;margin:0;position:absolute;right:1em;font-size:${fontSize}px;font-family:Roboto;">${priceFormat(saleObj?.price)}</p>
                </div>
                <div>
                    <p style="display:flex;font-size:${fontSize}px;margin:0;justify-content:center;">${saleObj?.date ?? ''}</p>
                </div>
            </body></html>`;

        win.document.write(html);
        win.document.close();

        const doPrint = () => {
            win.focus();
            win.print();
            win.onafterprint = () => win.close();
            setTimeout(() => { if (!win.closed) win.close(); }, 3000);
        };

        if (win.document.readyState === 'complete') {
            doPrint();
        } else {
            win.onload = doPrint;
        }
    };

    return (
        <>
            <Row style={{ width: '100%' }}>
                <Col>
                    {
                        lastUsages.length > 0 && currentUser?.showHistory && lastUsages.map((usageObj, i) => {
                            return (
                                <Row key={'usage_row_' + i} className='mb-10 p-10'>
                                    <Col className='col-auto'>
                                        <img
                                            style={{ width: 100 }}
                                            src={usageObj?.avatar || AVATAR_PLACEHOLDER}
                                            alt=''
                                        />
                                    </Col>
                                    <Col style={{ textAlign: 'left' }}>
                                        <b className='accent m-0'>{usageObj?.date}</b>
                                        <p className='m-0 mt-10'>{usageObj?.title}</p>
                                        <p className='m-0'>{usageObj?.code}</p>
                                        <p className='m-0'>{usageObj?.lastName}</p>
                                        <b className='m-0'>{usageObj?.firstName}</b>
                                    </Col>
                                </Row>
                            )
                        })
                    }
                </Col>
                <Col>
                    <div>
                        <Image
                            className="home-image"
                            src={renderHomeImage(saleStatusCode)} />

                        {renderHomeDescription(saleStatusCode)}

                        <Spinner animation="border" size='lg' style={{ visibility: loading ? 'visible' : 'hidden' }} />

                        <div className='card-result-container'>
                            {
                                saleUser && <div className='card-result-info-container'>
                                    <Image
                                        className="sale-avatar"
                                        src={saleUser?.avatar || AVATAR_PLACEHOLDER}
                                    />
                                    <div style={{ textAlign: 'left' }}>
                                        <p className='user-info m-0'>{saleUser?.title}</p>
                                        <p className='user-info m-0'>{saleUser?.code}</p>
                                        <p className='user-info m-0'>{saleUser?.lastName}</p>
                                        <p className='user-info bold m-0'>{saleUser?.firstName}</p>
                                        {
                                            sale && saleStatusCode === 'SUCCESS'
                                            && (sale.loanUsage
                                                ? <p className='sale-status sale-status-warning m-0'>Зээлийн эрхээ ашиглалаа.<br />{priceFormat(sale.balance)}₮<br />{sale?.quantity} зээлийн эрх үлдсэн байна</p>
                                                : <p className='sale-status m-0'>{sale?.quantity} эрх үлдсэн байна</p>)
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </Col>
                <Col>
                </Col>
            </Row>

            {process.env.REACT_APP_ENV === 'development' && (
                <div style={{ position: 'fixed', bottom: 16, right: 16, display: 'flex', gap: 8, zIndex: 9999 }}>
                    <input
                        type='text'
                        placeholder='Paste NFC code...'
                        value={devNfcInput}
                        onChange={e => setDevNfcInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && devNfcInput.trim()) {
                                submitUsage(devNfcInput.trim());
                                setDevNfcInput('');
                            }
                        }}
                        style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #aaa', width: 200 }}
                    />
                    <Button
                        size='sm'
                        variant='warning'
                        onClick={() => {
                            if (devNfcInput.trim()) {
                                submitUsage(devNfcInput.trim());
                                setDevNfcInput('');
                            }
                        }}
                    >
                        Submit
                    </Button>
                </div>
            )}

            <Modal
                show={saleDuplicateModal}
                centered
                backdrop="static"
                onHide={() => {
                    nfcCodeRef.current = '';
                    setSaleStatusCode(null);
                    setSale(null);
                    setSaleUser(null);
                    setSaleDuplicateModal(false);
                }}>
                <Modal.Body>
                    <Row>
                        <Col className={'text-center'}>
                            <Image
                                className="modal-image"
                                src={`/assets/images/duplicated.png`} />

                            <p className='basket-label m-0 modal-description text-brown'><span>Өнөөдөр хоолондоо орсон байна!</span><span>You have used today!</span></p>
                            <p className='basket-label m-0 modal-description'><span>Дахин уншуулах уу?</span><span>Are you sure to use again?</span></p>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: 30 }}>
                        <Col className="text-center" style={{ paddingLeft: 30, paddingRight: 30 }}>
                            <Button type={'button'} variant="secondary" className="btn-gray btn-modal-large"
                                onClick={() => {
                                    setNfcListeningBoth(true);
                                    nfcCodeRef.current = '';
                                    setSaleStatusCode(null);
                                    setSale(null);
                                    setSaleUser(null);
                                    setSaleDuplicateModal(false);
                                }}>
                                No
                            </Button>
                        </Col>
                        <Col className="text-center" style={{ paddingLeft: 30, paddingRight: 30 }}>
                            <Button type={'button'} className='btn-green  btn-modal-large' onClick={() => {
                                submitUsage(tempNfcCode, true);
                                setSaleDuplicateModal(false);
                            }}>
                                Yes
                            </Button>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
        </>
    );

}
export default FoodSale;
